import { Suspense, lazy } from "react";
import { createBrowserRouter } from "react-router-dom";

import Layout from "../Layout";
import { loader } from "./appLoader";
import { AdminUserAccessRequestLazy, AdminUserManagementLazy, AdminUserPreferencesLazy } from "./lazy/Admin";
import { ProfileLazy } from "./lazy/Profile";
import {
  ErrorLazy as ErrorLazyLoaded,
  LogoutLazy,
  UserAccountDeletedLazy,
  UserPendingApprovalLazy,
  UserSubmitForApprovalLazy,
} from "./lazy/SupportPages";
import { AncillaryAnalysisLazy } from "./lazy/ancillaries/Analysis";
import {
  AncillaryDefinitionsJourneyLazy,
  AncillaryDefinitionsLayoutLazy,
  AncillaryDefinitionsLazy,
  AncillaryDefinitionsOthersLazy,
  AncillaryDefinitionsSegmentLazy,
} from "./lazy/ancillaries/Definitions";
import {
  AncillaryOverridesCreateLazy,
  AncillaryOverridesDuplicateLazy,
  AncillaryOverridesEditLazy,
  AncillaryOverridesLayoutLazy,
  AncillaryOverridesLazy,
} from "./lazy/ancillaries/Overrides";
import {
  AncillaryPricingBoundsJourneyLazy,
  AncillaryPricingBoundsLayoutLazy,
  AncillaryPricingBoundsLazy,
  AncillaryPricingBoundsSegmentLazy,
} from "./lazy/ancillaries/PricingBounds";
import { AncillaryWTPApproachLazy } from "./lazy/ancillaries/WTPApproach";
import { BundleAnalysisLazy } from "./lazy/bundles/Analysis";
import {
  BundleOverridesCreateLazy,
  BundleOverridesLayoutLazy,
  BundleOverridesLazy,
} from "./lazy/bundles/BundleOverrides";
import {
  BundleDefinitionsCreateLazy,
  BundleDefinitionsDetailsLazy,
  BundleDefinitionsDuplicateLazy,
  BundleDefinitionsEditLazy,
  BundleDefinitionsFormLayoutLazy,
  BundleDefinitionsGridLayoutLazy,
  BundleDefinitionsImportLazy,
  BundleDefinitionsLayoutLazy,
  BundleDefinitionsLazy,
  BundleDefinitionsNonRTFLazy,
  BundleDefinitionsRTFLazy,
  ServiceDefinitionsImportLazy,
} from "./lazy/bundles/Definitions";
import { BundlePricingBoundsLazy } from "./lazy/bundles/PricingBounds";
import { RankOverridesLazy } from "./lazy/bundles/RankOverrides";
import { BundleRecommenderLazy } from "./lazy/bundles/Recommender";
import { BundleRecommenderConfigLazy } from "./lazy/bundles/RecommenderConfig";
import { BundleServiceHierarchyLazy } from "./lazy/bundles/ServiceHierarchy";
import { BundleWTPApproachLazy } from "./lazy/bundles/WTPApproach";

import {
  ROUTES_ADMIN,
  ROUTES_ADMIN_USER_MANAGEMENT,
  ROUTES_ADMIN_USER_MANAGEMENT_ACCESS_REQUESTS,
  ROUTES_ADMIN_USER_MANAGEMENT_PREFERENCES,
  ROUTES_ANCILLARIES,
  ROUTES_ANCILLARIES_ANALYSIS,
  ROUTES_ANCILLARIES_DEFINITIONS,
  ROUTES_ANCILLARIES_DEFINITIONS_JOURNEY,
  ROUTES_ANCILLARIES_DEFINITIONS_OTHERS,
  ROUTES_ANCILLARIES_DEFINITIONS_SEGMENT,
  ROUTES_ANCILLARIES_OVERRIDES,
  ROUTES_ANCILLARIES_OVERRIDES_CREATE,
  ROUTES_ANCILLARIES_OVERRIDES_DUPLICATE,
  ROUTES_ANCILLARIES_OVERRIDES_EDIT,
  ROUTES_ANCILLARIES_PRICING_BOUNDS,
  ROUTES_ANCILLARIES_PRICING_BOUNDS_JOURNEY,
  ROUTES_ANCILLARIES_PRICING_BOUNDS_SEGMENT,
  ROUTES_ANCILLARIES_WTP_APPROACH,
  ROUTES_BUNDLES,
  ROUTES_BUNDLES_ANALYSIS,
  ROUTES_BUNDLES_BUNDLE_OVERRIDES,
  ROUTES_BUNDLES_BUNDLE_OVERRIDES_CREATE,
  ROUTES_BUNDLES_CREATE,
  ROUTES_BUNDLES_DEFINITIONS,
  ROUTES_BUNDLES_DEFINITIONS_DETAILS,
  ROUTES_BUNDLES_DEFINITIONS_NON_RTF,
  ROUTES_BUNDLES_DEFINITIONS_RTF,
  ROUTES_BUNDLES_DUPLICATE,
  ROUTES_BUNDLES_EDIT,
  ROUTES_BUNDLES_IMPORT,
  ROUTES_BUNDLES_PRICING_BOUNDS,
  ROUTES_BUNDLES_RANK_OVERRIDES,
  ROUTES_BUNDLES_RECOMMENDER,
  ROUTES_BUNDLES_RECOMMENDER_CONFIG,
  ROUTES_BUNDLES_SERVICE_HIERARCHY,
  ROUTES_BUNDLES_WTP_APPROACH,
  ROUTES_DELETED_USER,
  ROUTES_DISALLOWED_USER,
  ROUTES_HOME,
  ROUTES_LOGOUT,
  ROUTES_PROFILE,
  ROUTES_SERVICES_IMPORT,
  ROUTES_UNAUTHORIZED_USER,
} from "@utils/Constants";

import { AuthProvider } from "@context/authorization/AuthProvider";

const ErrorLazy = lazy(() => import("@workflows/support/ErrorPage"));

const AppRoutes = createBrowserRouter([
  {
    path: ROUTES_HOME,
    element: (
      <AuthProvider>
        <Layout />
      </AuthProvider>
    ),
    errorElement: (
      <Suspense fallback={<div>Loading...</div>}>
        <ErrorLazy />
      </Suspense>
    ),
    loader: loader,
    id: "root",
    children: [
      {
        path: ROUTES_HOME,
      },
      {
        path: ROUTES_ANCILLARIES,
        children: [
          {
            lazy: AncillaryDefinitionsLayoutLazy,
            children: [
              {
                path: ROUTES_ANCILLARIES_DEFINITIONS,
                lazy: AncillaryDefinitionsLazy,
              },
              {
                path: ROUTES_ANCILLARIES_DEFINITIONS_JOURNEY,
                lazy: AncillaryDefinitionsJourneyLazy,
              },
              {
                path: ROUTES_ANCILLARIES_DEFINITIONS_SEGMENT,
                lazy: AncillaryDefinitionsSegmentLazy,
              },
              {
                path: ROUTES_ANCILLARIES_DEFINITIONS_OTHERS,
                lazy: AncillaryDefinitionsOthersLazy,
              },
            ],
          },
          {
            path: ROUTES_ANCILLARIES_PRICING_BOUNDS,
            lazy: AncillaryPricingBoundsLayoutLazy,
            children: [
              {
                path: ROUTES_ANCILLARIES_PRICING_BOUNDS,
                lazy: AncillaryPricingBoundsLazy,
              },
              {
                path: ROUTES_ANCILLARIES_PRICING_BOUNDS_JOURNEY,
                lazy: AncillaryPricingBoundsJourneyLazy,
              },
              {
                path: ROUTES_ANCILLARIES_PRICING_BOUNDS_SEGMENT,
                lazy: AncillaryPricingBoundsSegmentLazy,
              },
            ],
          },
          {
            path: ROUTES_ANCILLARIES_WTP_APPROACH,
            lazy: AncillaryWTPApproachLazy,
          },
          {
            path: ROUTES_ANCILLARIES_ANALYSIS,
            lazy: AncillaryAnalysisLazy,
          },
          {
            path: ROUTES_ANCILLARIES_OVERRIDES,
            lazy: AncillaryOverridesLayoutLazy,
            children: [
              {
                index: true,
                lazy: AncillaryOverridesLazy,
              },
              {
                path: ROUTES_ANCILLARIES_OVERRIDES_CREATE,
                lazy: AncillaryOverridesCreateLazy,
              },
              {
                path: ROUTES_ANCILLARIES_OVERRIDES_EDIT,
                lazy: AncillaryOverridesEditLazy,
              },
              {
                path: ROUTES_ANCILLARIES_OVERRIDES_DUPLICATE,
                lazy: AncillaryOverridesDuplicateLazy,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES_BUNDLES,
        children: [
          {
            lazy: BundleDefinitionsLayoutLazy,
            children: [
              {
                lazy: BundleDefinitionsGridLayoutLazy,
                children: [
                  {
                    path: ROUTES_BUNDLES_DEFINITIONS,
                    lazy: BundleDefinitionsLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_DEFINITIONS_RTF,
                    lazy: BundleDefinitionsRTFLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_DEFINITIONS_NON_RTF,
                    lazy: BundleDefinitionsNonRTFLazy,
                  },
                ],
              },
              {
                lazy: BundleDefinitionsFormLayoutLazy,
                children: [
                  {
                    path: ROUTES_BUNDLES_DEFINITIONS_DETAILS,
                    lazy: BundleDefinitionsDetailsLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_CREATE,
                    lazy: BundleDefinitionsCreateLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_EDIT,
                    lazy: BundleDefinitionsEditLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_DUPLICATE,
                    lazy: BundleDefinitionsDuplicateLazy,
                  },
                  {
                    path: ROUTES_BUNDLES_IMPORT,
                    lazy: BundleDefinitionsImportLazy,
                  },
                  {
                    path: ROUTES_SERVICES_IMPORT,
                    lazy: ServiceDefinitionsImportLazy,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTES_BUNDLES_SERVICE_HIERARCHY,
            lazy: BundleServiceHierarchyLazy,
          },
          {
            path: ROUTES_BUNDLES_PRICING_BOUNDS,
            lazy: BundlePricingBoundsLazy,
          },
          {
            path: ROUTES_BUNDLES_RECOMMENDER,
            lazy: BundleRecommenderLazy,
          },
          {
            path: ROUTES_BUNDLES_WTP_APPROACH,
            lazy: BundleWTPApproachLazy,
          },
          {
            path: ROUTES_BUNDLES_RECOMMENDER_CONFIG,
            lazy: BundleRecommenderConfigLazy,
          },
          {
            path: ROUTES_BUNDLES_ANALYSIS,
            lazy: BundleAnalysisLazy,
          },
          {
            path: ROUTES_BUNDLES_RANK_OVERRIDES,
            lazy: RankOverridesLazy,
          },
          {
            path: ROUTES_BUNDLES_BUNDLE_OVERRIDES,
            lazy: BundleOverridesLayoutLazy,
            children: [
              {
                index: true,
                lazy: BundleOverridesLazy,
              },
              {
                path: ROUTES_BUNDLES_BUNDLE_OVERRIDES_CREATE,
                lazy: BundleOverridesCreateLazy,
              },
            ],
          },
        ],
      },
      {
        path: ROUTES_ADMIN,
        children: [
          {
            index: true,
            lazy: AdminUserManagementLazy,
          },
          {
            path: ROUTES_ADMIN_USER_MANAGEMENT,
            lazy: AdminUserManagementLazy,
          },
          {
            path: ROUTES_ADMIN_USER_MANAGEMENT_ACCESS_REQUESTS,
            lazy: AdminUserAccessRequestLazy,
          },
          {
            path: ROUTES_ADMIN_USER_MANAGEMENT_PREFERENCES,
            lazy: AdminUserPreferencesLazy,
          },
        ],
      },
      {
        path: ROUTES_PROFILE,
        lazy: ProfileLazy,
      },
    ],
  },
  {
    path: ROUTES_LOGOUT,
    lazy: LogoutLazy,
  },
  {
    path: ROUTES_UNAUTHORIZED_USER,
    lazy: UserPendingApprovalLazy,
  },
  {
    path: ROUTES_DISALLOWED_USER,
    lazy: UserSubmitForApprovalLazy,
  },
  {
    path: ROUTES_DELETED_USER,
    lazy: UserAccountDeletedLazy,
  },
  {
    path: "*",
    lazy: ErrorLazyLoaded,
  },
]);

export default AppRoutes;
